export const customerStatusValues = ['active', 'inactive'] as const;
export type CustomerStatus = (typeof customerStatusValues)[number];

export const customerSyncStatusValues = ['never_synced', 'pending', 'synced', 'failed'] as const;
export type CustomerSyncStatus = (typeof customerSyncStatusValues)[number];

export const customerIdentifierTypeValues = ['ca_id', 'license_plate'] as const;
export type CustomerIdentifierType = (typeof customerIdentifierTypeValues)[number];

export const customerIdentifierStatusValues = ['active', 'inactive'] as const;
export type CustomerIdentifierStatus = (typeof customerIdentifierStatusValues)[number];

export interface CustomerDto {
  id: string;
  companyId: string;
  displayName: string;
  status: CustomerStatus;
  isPublic: boolean;
  createdAt: string;
  updatedAt: string;
  syncStatus?: CustomerSyncStatus;
  syncError?: string | null;
}

export interface CustomerIdentifierDto {
  id: string;
  companyId: string;
  customerId: string;
  type: CustomerIdentifierType;
  valueMasked: string;
  status: CustomerIdentifierStatus;
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
  syncStatus?: CustomerSyncStatus;
  syncError?: string | null;
}

export interface CustomerIdentifierSnapshot {
  type: CustomerIdentifierType;
  value: string;
}

export interface CustomerSnapshot {
  name: string;
}

export interface CustomerIdentifierMatchDto {
  customerId: string;
  displayName: string;
  identifierId: string;
}

export interface RecordPurchaseReassignmentRequest {
  companyId: string;
  actorUserId: string;
  receiptNumber: string;
  purchaseId?: string | null;
  occurredAt?: string;
  action?: string;
  auditId?: string;
}

export interface RecordPurchaseReassignmentResponse {
  recorded: true;
}

export interface CreateCustomerRequest {
  companyId: string;
  displayName: string;
}

export interface CreateCustomerResponse {
  customer: CustomerDto;
}

export interface UpdateCustomerRequest {
  companyId: string;
  customerId: string;
  displayName?: string;
}

export interface UpdateCustomerResponse {
  customer: CustomerDto;
}

export interface DeactivateCustomerRequest {
  companyId: string;
  customerId: string;
}

export interface DeactivateCustomerResponse {
  customer: CustomerDto;
}

export interface GetCustomerRequest {
  companyId: string;
  customerId: string;
}

export interface GetCustomerResponse {
  customer: CustomerDto;
}

export interface ListCustomersRequest {
  companyId: string;
  status?: CustomerStatus;
}

export interface ListCustomersResponse {
  items: CustomerDto[];
}

export interface AddCustomerIdentifierRequest {
  companyId: string;
  customerId: string;
  type: CustomerIdentifierType;
  value: string;
  isDefault?: boolean;
}

export interface AddCustomerIdentifierResponse {
  identifier: CustomerIdentifierDto;
}

export interface UpdateCustomerIdentifierRequest {
  companyId: string;
  customerId: string;
  identifierId: string;
  value?: string;
}

export interface UpdateCustomerIdentifierResponse {
  identifier: CustomerIdentifierDto;
}

export interface DeactivateCustomerIdentifierRequest {
  companyId: string;
  customerId: string;
  identifierId: string;
}

export interface DeactivateCustomerIdentifierResponse {
  identifier: CustomerIdentifierDto;
}

export interface SetDefaultCustomerIdentifierRequest {
  companyId: string;
  customerId: string;
  identifierId: string;
}

export interface SetDefaultCustomerIdentifierResponse {
  identifier: CustomerIdentifierDto;
}

export interface ListCustomerIdentifiersRequest {
  companyId: string;
  customerId: string;
  status?: CustomerIdentifierStatus;
}

export interface ListCustomerIdentifiersResponse {
  items: CustomerIdentifierDto[];
}

export interface ResolveCustomerMatchByIdentifierRequest {
  companyId: string;
  identifierType: CustomerIdentifierType;
  identifierValue: string;
}

export interface ResolveCustomerMatchByIdentifierResponse {
  match: CustomerIdentifierMatchDto | null;
  needsOperatorConfirmation: boolean;
}

export interface CustomerSnapshotForPurchaseRequest {
  requiresCustomerName: boolean;
  customerDisplayName: string;
  identifierSnapshot?: CustomerIdentifierSnapshot | null;
}

export interface CustomerSnapshotForPurchaseResponse {
  customerSnapshot: CustomerSnapshot | null;
  identifierSnapshot: CustomerIdentifierSnapshot | null;
}
